import dayjs from 'dayjs';
import React from 'react';
import { useIntl } from 'react-intl';

import { AddressInput } from '@/components/common/AddressInput';

import { DatesField } from './DatesField';
import { GuestsField, TGuestOccupancyValue } from './GuestsField';
import { GuidedSearchComponentField } from './GuidedSearchComponentField';
import { NumericInputField } from './NumericInputField';
import {
  EGuidedSearchComponentType,
  TDatePickerComponentFieldValue,
  TGuestOccupancyConfig,
  TGuidedSearchComponent,
  TGuidedSearchComponentField,
} from './types';
import { UpscaleGuidedSearchComponentField } from './UpscaleGuidedSearchComponentField';

type TGuidedSearchComponentProps = TGuidedSearchComponent & {
  properties: {
    title: string;
  };
  values: Record<string, undefined | string | string[]>;
  onChange: (fieldId: string, value?: string | string[]) => void;
  isUpscale?: boolean;
  // Default location for an address_input field (e.g. the locality's address).
  defaultAddress?: string;
};

// A date_range_picker value is stored as a positional string[] aligned with the
// field's JSON query_params: [from (YYYY-MM-DD), to (YYYY-MM-DD), flexible_days].
// Empty strings are emitted for parts that should contribute no query param.
const serializeDates = (value?: TDatePickerComponentFieldValue): string[] => {
  // Drop incomplete ranges entirely so they produce no date params.
  if (!value?.from || !value?.to) return ['', '', ''];
  return [
    dayjs(value.from).format('YYYY-MM-DD'),
    dayjs(value.to).format('YYYY-MM-DD'),
    value.flexible_days != null ? String(value.flexible_days) : '',
  ];
};

const deserializeDates = (raw?: string | string[]): TDatePickerComponentFieldValue | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const [from, to, flexible] = raw;
  if (!from || !to) return undefined;
  return {
    from: dayjs(from).toDate(),
    to: dayjs(to).toDate(),
    ...(flexible ? { flexible_days: Number(flexible) } : {}),
  };
};

// A guest_occupancy value is stored as a positional string[] aligned with the
// field's guests config: [adults, children, pet]. `pet` holds the pet queryValue
// (e.g. "pet_friendly") when on, or an empty string when off.
const serializeGuests = (value: TGuestOccupancyValue, config?: TGuestOccupancyConfig): string[] => [
  String(value.adults),
  String(value.children),
  value.pet ? (config?.pet?.queryValue ?? '') : '',
];

const deserializeGuests = (
  raw: string | string[] | undefined,
  config?: TGuestOccupancyConfig,
): TGuestOccupancyValue => {
  const [adults, children, pet] = Array.isArray(raw) ? raw : [];
  const adultsDefault = config?.adults?.defaultValue ?? config?.adults?.minValue ?? 1;
  const childrenDefault = config?.children?.defaultValue ?? 0;
  return {
    adults: adults ? Number(adults) : adultsDefault,
    children: children ? Number(children) : childrenDefault,
    pet: !!pet,
  };
};

export const GuidedSearchComponent = ({
  fields,
  properties,
  values,
  onChange,
  isUpscale,
  defaultAddress,
}: TGuidedSearchComponentProps) => {
  const intl = useIntl();
  const FieldComponent = isUpscale ? UpscaleGuidedSearchComponentField : GuidedSearchComponentField;

  const locationLabel = intl.formatMessage({
    id: 'duuWHQ',
    defaultMessage: 'Location',
    description: 'Locality guided search > Initial step > Location field label',
  });

  const renderField = (field: TGuidedSearchComponentField) => {
    const stored = values[field.id];

    switch (field.component_type) {
      case EGuidedSearchComponentType.AddressInput:
        return (
          <AddressInput
            key={field.id}
            label={field.properties.label ?? locationLabel}
            inputValue={typeof stored === 'string' ? stored : (defaultAddress ?? '')}
            onChange={value => onChange(field.id, value)}
          />
        );
      case EGuidedSearchComponentType.DateRangePicker:
        return (
          <DatesField
            key={field.id}
            value={deserializeDates(stored)}
            onChange={dates => onChange(field.id, serializeDates(dates))}
          />
        );
      case EGuidedSearchComponentType.NumericInput: {
        // Fully JSON-driven: label, bounds and initial value come from properties.
        const { title, minValue, maxValue, defaultValue } = field.properties;
        const min = minValue != null ? Number(minValue) : undefined;
        const fallback = Number(defaultValue ?? minValue ?? 0);
        const current = typeof stored === 'string' && stored !== '' ? Number(stored) : fallback;

        return (
          <NumericInputField
            key={field.id}
            label={title ?? field.id}
            value={current}
            minValue={min}
            maxValue={maxValue != null ? Number(maxValue) : undefined}
            onChange={next => onChange(field.id, String(next))}
          />
        );
      }
      case EGuidedSearchComponentType.GuestOccupancy: {
        const config = field.properties.guests ?? {};
        return (
          <GuestsField
            key={field.id}
            config={config}
            value={deserializeGuests(stored, config)}
            onChange={next => onChange(field.id, serializeGuests(next, config))}
          />
        );
      }
      case EGuidedSearchComponentType.ButtonGroupRadio:
        return (
          <FieldComponent
            key={field.id}
            value={field.id in values ? values[field.id] : undefined}
            onChange={value => onChange(field.id, value)}
            {...field}
          />
        );
      default:
        // Unsupported/unknown field type — render nothing.
        return null;
    }
  };

  // Row key for grouping consecutive fields into a shared 2-col row. Dates and
  // Guests are compact single triggers that sit side-by-side, so they share a key;
  // same-type numeric inputs still pair; everything else gets its own full-width row.
  const rowKey = (field: TGuidedSearchComponentField): string => {
    switch (field.component_type) {
      case EGuidedSearchComponentType.DateRangePicker:
      case EGuidedSearchComponentType.GuestOccupancy:
        return 'dates-guests';
      case EGuidedSearchComponentType.NumericInput:
        return 'numeric';
      default:
        return field.id;
    }
  };

  const fieldGroups = fields.reduce<TGuidedSearchComponentField[][]>((groups, field) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup[0] && rowKey(lastGroup[0]) === rowKey(field)) {
      lastGroup.push(field);
    } else {
      groups.push([field]);
    }
    return groups;
  }, []);

  return (
    <div className={`flex flex-col ${properties?.title ? 'gap-6' : 'gap-3'}`}>
      {fieldGroups.map(group => {
        const [first] = group;
        if (!first) return null;

        return group.length > 1 ? (
          <div key={first.id} className="grid grid-cols-2 gap-3">
            {group.map(renderField)}
          </div>
        ) : (
          renderField(first)
        );
      })}
    </div>
  );
};
